import * as React from 'react'
import {
  TextB,
  TextItalic,
  TextUnderline,
  TextStrikethrough,
  ListBullets,
  ListNumbers,
  LinkSimple,
  Paperclip,
  Quotes,
  PaperPlaneTilt,
  type Icon,
} from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { Button } from './button'
import { InputGroup, InputGroupAddon, InputGroupButton } from './input-group'
import { type FieldStatus } from './field'

/**
 * RichTextEditor — the *chrome* for a rich-text field: a formatting toolbar over
 * an editable surface, built on the `InputGroup` shell (shared border + focus
 * ring), a `block-start` toolbar row, and `InputGroupButton`s. It bundles **no
 * editing engine** — the toolbar buttons emit `onCommand(command)` and the
 * surface is a plain `contentEditable`, so the consuming app wires its own
 * engine (Quill, TipTap, Lexical, …) to those hooks. This keeps the design
 * system free of a heavy editor dependency while owning the look.
 *
 *   <RichTextEditor
 *     placeholder="Write a description…"
 *     activeCommands={['bold']}
 *     onCommand={(cmd) => editor.toggle(cmd)}
 *   />
 *
 * Pass `toolbar` to replace the default button set (compose your own with
 * `RichTextEditorButton` + `RichTextEditorDivider`). Grab the surface via
 * `contentRef` to mount an engine onto it. Set `onSend` / `onDiscard` to render
 * a footer with Send (primary) + Discard (ghost) actions, or pass `footer` for a
 * fully custom action row (e.g. an attachment control beside Send).
 *
 * Placeholder note: it's the CSS `:empty` pseudo on the surface, so to reset the
 * editor and restore the placeholder, clear `innerHTML` — not just the text —
 * since a leftover `<br>` keeps the element from matching `:empty`.
 */

export type RichTextCommand =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikethrough'
  | 'bulletList'
  | 'orderedList'
  | 'link'
  | 'attachment'
  | 'blockquote'
  | 'code'

interface ToolbarItem {
  command: RichTextCommand
  icon: Icon
  label: string
  /** Start of a new visual group — render a divider before it. */
  group?: boolean
}

const DEFAULT_ITEMS: ToolbarItem[] = [
  { command: 'bold', icon: TextB, label: 'Bold' },
  { command: 'italic', icon: TextItalic, label: 'Italic' },
  { command: 'underline', icon: TextUnderline, label: 'Underline' },
  { command: 'strikethrough', icon: TextStrikethrough, label: 'Strikethrough' },
  { command: 'bulletList', icon: ListBullets, label: 'Bulleted list', group: true },
  { command: 'orderedList', icon: ListNumbers, label: 'Numbered list' },
  { command: 'link', icon: LinkSimple, label: 'Link', group: true },
  { command: 'attachment', icon: Paperclip, label: 'Attach file' },
  // No code-block button: CRM notes/emails never contain code, and it just adds
  // toolbar noise. `blockquote` stays — quoting is useful in email replies.
  { command: 'blockquote', icon: Quotes, label: 'Quote', group: true },
]

/** A thin vertical rule separating toolbar groups. */
export const RichTextEditorDivider = () => (
  <span aria-hidden="true" className="mx-1 h-5 w-px self-center bg-fbui-gray-200" />
)
RichTextEditorDivider.displayName = 'RichTextEditorDivider'

export interface RichTextEditorButtonProps
  extends React.ComponentPropsWithoutRef<typeof InputGroupButton> {
  /** Render the button pressed (sets `aria-pressed` + a tinted background). */
  active?: boolean
}

/**
 * A toolbar button — an `InputGroupButton` with a pressed/active state. Use it
 * to build a custom `toolbar`.
 */
export const RichTextEditorButton = React.forwardRef<
  HTMLButtonElement,
  RichTextEditorButtonProps
>(({ active, className, size = 'icon-sm', ...props }, ref) => (
  <InputGroupButton
    ref={ref}
    size={size}
    aria-pressed={active}
    className={cn(active && 'bg-fbui-gray-100 text-fbui-gray-700', className)}
    {...props}
  />
))
RichTextEditorButton.displayName = 'RichTextEditorButton'

/** One selectable @mention (a teammate). `id` is written into the mention
 *  token's `data-id` (what the backend parses); `label` is the shown name. */
export interface MentionItem {
  id: string | number
  label: string
}

/** Wiring for the `@`-mention autocomplete. The design system owns the trigger +
 *  menu + token insertion; the app owns *who* is mentionable via `search` (it
 *  filters/limits — e.g. excludes the current user, caps the list). */
export interface RichTextMentionsConfig {
  /** Matches for the text typed after the trigger, called on each keystroke. */
  search: (query: string) => MentionItem[]
  /** Trigger character (default `@`). */
  trigger?: string
}

/** Merge several refs (object + callback) into one callback ref. */
function setRefs<T>(...refs: (React.Ref<T> | undefined)[]) {
  return (value: T | null) => {
    for (const ref of refs) {
      if (!ref) continue
      if (typeof ref === 'function') ref(value)
      else (ref as React.MutableRefObject<T | null>).current = value
    }
  }
}

const MENTION_NAV_KEYS = ['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape']

/**
 * The `@`-mention engine for the editable surface: watches the caret for a
 * `trigger + query` run, shows a keyboard-navigable menu of `search(query)`
 * matches, and on select replaces the run with an atomic mention token
 * (`<span class="mention" data-id … contenteditable="false">@Name</span>` + a
 * trailing space) — the same serialization the app's legacy editor produced, so
 * the backend parses mentions identically. No-ops entirely when `config` is
 * omitted. Returns handlers to spread on the surface + the menu node to render.
 */
function useMentions(
  getEl: () => HTMLDivElement | null,
  config?: RichTextMentionsConfig
) {
  const enabled = !!config
  const trigger = config?.trigger ?? '@'
  const [open, setOpen] = React.useState(false)
  const [items, setItems] = React.useState<MentionItem[]>([])
  const [active, setActive] = React.useState(0)
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null)
  // The mention run captured when the menu opened. Selecting reads THIS, not the
  // live selection — a mouse click on the menu would otherwise lose the caret.
  const ctxRef = React.useRef<{
    node: Node
    at: number
    caret: number
  } | null>(null)

  const close = React.useCallback(() => {
    setOpen(false)
    setItems([])
    setActive(0)
    ctxRef.current = null
  }, [])

  // The active mention run: from the last unglued trigger char to the caret,
  // provided that run has no whitespace. Null when the caret isn't in one.
  const readContext = React.useCallback(() => {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return null
    const range = sel.getRangeAt(0)
    const node = range.startContainer
    if (node.nodeType !== Node.TEXT_NODE) return null
    const before = (node.textContent ?? '').slice(0, range.startOffset)
    const at = before.lastIndexOf(trigger)
    if (at === -1) return null
    const prev = at > 0 ? before[at - 1] : ' '
    if (prev && !/\s/.test(prev)) return null // glued to a word (e-mail, etc.)
    const query = before.slice(at + trigger.length)
    if (/\s/.test(query)) return null
    return { node, at, caret: range.startOffset, query }
  }, [trigger])

  const refresh = React.useCallback(() => {
    if (!enabled) return
    const ctx = readContext()
    if (!ctx) return close()
    const matches = config!.search(ctx.query)
    if (!matches.length) return close()
    // Snapshot the run so a mouse-selected item inserts even after the caret is
    // lost to the click.
    ctxRef.current = { node: ctx.node, at: ctx.at, caret: ctx.caret }
    setItems(matches)
    setActive(0)
    const sel = window.getSelection()
    if (sel && sel.rangeCount) {
      const r = sel.getRangeAt(0).getBoundingClientRect()
      setPos({ top: r.bottom + 4, left: r.left })
    }
    setOpen(true)
  }, [enabled, readContext, config, close])

  const select = React.useCallback(
    (item: MentionItem) => {
      const el = getEl()
      // Prefer the snapshot (survives a mouse click); fall back to live read.
      const ctx = ctxRef.current ?? readContext()
      if (!el || !ctx) return close()
      const range = document.createRange()
      range.setStart(ctx.node, ctx.at)
      range.setEnd(ctx.node, ctx.caret)
      range.deleteContents()
      const span = document.createElement('span')
      span.className = 'mention'
      span.setAttribute('data-denotation-char', trigger)
      span.setAttribute('data-id', String(item.id))
      span.setAttribute('data-value', item.label)
      span.setAttribute('contenteditable', 'false')
      span.textContent = `${trigger}${item.label}`
      range.insertNode(span)
      const space = document.createTextNode(' ')
      span.after(space)
      const after = document.createRange()
      after.setStartAfter(space)
      after.collapse(true)
      const sel = window.getSelection()
      sel?.removeAllRanges()
      sel?.addRange(after)
      close()
      // Let consumers (empty-state tracking, etc.) see the change.
      el.dispatchEvent(new Event('input', { bubbles: true }))
      el.focus()
    },
    [getEl, readContext, trigger, close]
  )

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (!open) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActive((i) => (i + 1) % items.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActive((i) => (i - 1 + items.length) % items.length)
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        select(items[active])
      } else if (e.key === 'Escape') {
        e.preventDefault()
        close()
      }
    },
    [open, items, active, select, close]
  )

  const onKeyUp = React.useCallback(
    (e: React.KeyboardEvent) => {
      // Nav keys are handled on keydown; never let their keyup run refresh — in
      // particular Escape closes on keydown, so by keyup `open` is already false
      // and an `open`-gated guard would miss it, re-opening the menu.
      if (MENTION_NAV_KEYS.includes(e.key)) return
      refresh()
    },
    [refresh]
  )

  const menu =
    enabled && open && items.length ? (
      <ul
        role="listbox"
        aria-label="Mention a teammate"
        className="fbui-scroll fixed z-50 max-h-56 w-56 overflow-y-auto rounded-lg border border-fbui-gray-200 bg-fbui-base-white py-1 shadow-fbui-md"
        style={{ top: pos?.top, left: pos?.left }}
      >
        {items.map((item, i) => (
          <li key={item.id}>
            <button
              type="button"
              role="option"
              aria-selected={i === active}
              // Keep the caret in the surface (mousedown fires before blur).
              onMouseDown={(e) => {
                e.preventDefault()
                select(item)
              }}
              onMouseEnter={() => setActive(i)}
              className={cn(
                'flex w-full items-center px-3 py-1.5 text-left text-sm text-fbui-gray-700',
                i === active ? 'bg-fbui-gray-100' : 'hover:bg-fbui-gray-50'
              )}
            >
              {item.label}
            </button>
          </li>
        ))}
      </ul>
    ) : null

  return { menu, onKeyDown, onKeyUp, onBlur: close }
}

export interface RichTextEditorProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Border + ring color. Inherited from a parent `<Field>` when omitted. */
  status?: FieldStatus
  /** Disable the toolbar + make the surface read-only. */
  disabled?: boolean
  /** Placeholder shown while the surface is empty. */
  placeholder?: string
  /** Replace the default toolbar (compose with `RichTextEditorButton`). */
  toolbar?: React.ReactNode
  /** Fired by the default toolbar buttons with the command name. */
  onCommand?: (command: RichTextCommand) => void
  /** Command names to render as pressed on the default toolbar. */
  activeCommands?: RichTextCommand[]
  /** Accessible name for the editable surface. */
  contentLabel?: string
  /** Ref to the `contentEditable` surface (mount an engine here). */
  contentRef?: React.Ref<HTMLDivElement>
  /** Classes for the editable surface (e.g. a taller `min-h`). */
  contentClassName?: string
  /** Enable `@`-mention autocomplete (the app supplies who's mentionable). */
  mentions?: RichTextMentionsConfig
  /** Fired by the footer Send action. Renders the footer when set. */
  onSend?: () => void
  /** Fired by the footer Discard action. Renders the footer when set. */
  onDiscard?: () => void
  /** Send button label. */
  sendLabel?: string
  /** Discard button label. */
  discardLabel?: string
  /** Disable just the Send action (e.g. while the editor is empty). */
  sendDisabled?: boolean
  /** Replace the default footer actions (e.g. add an attachment control). */
  footer?: React.ReactNode
  /** Content pinned to the footer's left edge (e.g. attachment chips), while
   *  the Send/Discard actions stay right-aligned. Renders the footer when set. */
  footerStart?: React.ReactNode
}

export const RichTextEditor = React.forwardRef<
  HTMLDivElement,
  RichTextEditorProps
>(
  (
    {
      status,
      disabled = false,
      placeholder,
      toolbar,
      onCommand,
      activeCommands,
      contentLabel = 'Rich text editor',
      contentRef,
      contentClassName,
      mentions,
      onSend,
      onDiscard,
      sendLabel = 'Send',
      discardLabel = 'Discard',
      sendDisabled = false,
      footer,
      footerStart,
      className,
      ...props
    },
    ref
  ) => {
    const hasFooter =
      footer != null ||
      footerStart != null ||
      onSend != null ||
      onDiscard != null

    // @-mention engine (no-op unless `mentions` is set). It owns an internal ref
    // to the surface, composed with the caller's `contentRef`.
    const surfaceRef = React.useRef<HTMLDivElement | null>(null)
    const getSurface = React.useCallback(() => surfaceRef.current, [])
    const {
      menu: mentionMenu,
      onKeyDown: mentionKeyDown,
      onKeyUp: mentionKeyUp,
      onBlur: mentionBlur,
    } = useMentions(getSurface, mentions)
    const composedContentRef = React.useMemo(
      () => setRefs(surfaceRef, contentRef),
      [contentRef]
    )
    const defaultToolbar = DEFAULT_ITEMS.map(({ command, icon: Glyph, label, group }) => (
      <React.Fragment key={command}>
        {group && <RichTextEditorDivider />}
        <RichTextEditorButton
          aria-label={label}
          active={activeCommands?.includes(command)}
          // Keep focus in the surface so a command applies to the selection.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onCommand?.(command)}
        >
          <Glyph aria-hidden="true" />
        </RichTextEditorButton>
      </React.Fragment>
    ))

    return (
      <InputGroup
        ref={ref}
        status={status}
        disabled={disabled}
        className={className}
        {...props}
      >
        <InputGroupAddon
          align="block-start"
          divided
          role="toolbar"
          aria-label="Text formatting"
          className="flex-wrap gap-0.5 px-1.5 py-1"
        >
          {toolbar ?? defaultToolbar}
        </InputGroupAddon>
        <div
          ref={composedContentRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={contentLabel}
          aria-disabled={disabled || undefined}
          aria-placeholder={placeholder}
          data-placeholder={placeholder}
          onKeyDown={mentions ? mentionKeyDown : undefined}
          onKeyUp={mentions ? mentionKeyUp : undefined}
          onBlur={mentions ? mentionBlur : undefined}
          className={cn(
            'fbui-scroll max-h-80 min-h-28 basis-full px-3 py-2 text-sm text-fbui-gray-800 outline-none',
            // Placeholder via the empty pseudo-element (no value to bind on a DOM-owned surface).
            'empty:before:pointer-events-none empty:before:text-fbui-gray-400 empty:before:content-[attr(data-placeholder)]',
            // Rich-content rendering — the app's CSS reset strips list markers,
            // so re-assert them (plus blockquote / code / link styling) on the
            // formatted content the toolbar produces.
            '[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5',
            '[&_blockquote]:my-1 [&_blockquote]:border-l-2 [&_blockquote]:border-fbui-gray-300 [&_blockquote]:pl-3 [&_blockquote]:text-fbui-gray-600',
            '[&_pre]:my-1 [&_pre]:rounded [&_pre]:bg-fbui-gray-100 [&_pre]:p-2 [&_pre]:font-mono [&_pre]:text-xs',
            '[&_a]:text-fbui-basil-600 [&_a]:underline',
            // @-mention token chip.
            '[&_.mention]:rounded [&_.mention]:bg-fbui-basil-50 [&_.mention]:px-1 [&_.mention]:py-0.5 [&_.mention]:font-medium [&_.mention]:text-fbui-basil-700',
            disabled && 'cursor-not-allowed text-fbui-text-disabled',
            contentClassName
          )}
        />
        {mentionMenu}
        {hasFooter && (
          <InputGroupAddon
            align="block-end"
            divided
            className={cn(
              'gap-2',
              footerStart != null ? 'justify-between' : 'justify-end'
            )}
          >
            {footerStart != null && (
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                {footerStart}
              </div>
            )}
            {footer ?? (
              <div className="flex shrink-0 items-center gap-2">
                {onDiscard && (
                  <Button
                    type="button"
                    // Neutral (plain gray hover) — discard shouldn't read as a
                    // brand-positive action (ghost's green) nor shout in red.
                    variant="neutral"
                    size="sm"
                    disabled={disabled}
                    onClick={onDiscard}
                  >
                    {discardLabel}
                  </Button>
                )}
                {onSend && (
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={disabled || sendDisabled}
                    onClick={onSend}
                    leftIcon={<PaperPlaneTilt aria-hidden="true" />}
                  >
                    {sendLabel}
                  </Button>
                )}
              </div>
            )}
          </InputGroupAddon>
        )}
      </InputGroup>
    )
  }
)
RichTextEditor.displayName = 'RichTextEditor'
